/**
 * Towns a visitor can search around, with their coordinates.
 *
 * <p>A fixed list rather than a geocoder because there isn't one — the backend normalises postcode
 * strings but never resolves an address to a point. Adding Nominatim would mean an external
 * dependency, its usage policy, and a rate limit on a path that runs on every search; a list of
 * the places this product actually serves costs nothing and covers the realistic cases. If
 * arbitrary postcode search becomes worth it, this is the seam to replace.
 *
 * <p>Ordered by rough relevance to UK Muslim marriage events, not alphabetically — the list is
 * short enough to scan and the common choices belong at the top.
 */
export interface Town {
  name: string;
  lat: number;
  lng: number;
}

export const UK_TOWNS: Town[] = [
  { name: 'London', lat: 51.5074, lng: -0.1278 },
  { name: 'Birmingham', lat: 52.4862, lng: -1.8904 },
  { name: 'Manchester', lat: 53.4808, lng: -2.2426 },
  { name: 'Bradford', lat: 53.796, lng: -1.7594 },
  { name: 'Leeds', lat: 53.8008, lng: -1.5491 },
  { name: 'Leicester', lat: 52.6369, lng: -1.1398 },
  { name: 'Luton', lat: 51.8787, lng: -0.42 },
  { name: 'Blackburn', lat: 53.7486, lng: -2.487 },
  { name: 'Bolton', lat: 53.5769, lng: -2.4282 },
  { name: 'Oldham', lat: 53.5409, lng: -2.1114 },
  { name: 'Nottingham', lat: 52.9548, lng: -1.1581 },
  { name: 'Sheffield', lat: 53.3811, lng: -1.4701 },
  { name: 'Coventry', lat: 52.4068, lng: -1.5197 },
  { name: 'Derby', lat: 52.9226, lng: -1.4746 },
  { name: 'Slough', lat: 51.5105, lng: -0.595 },
  { name: 'Liverpool', lat: 53.4084, lng: -2.9916 },
  { name: 'Preston', lat: 53.7632, lng: -2.7031 },
  { name: 'Peterborough', lat: 52.5695, lng: -0.2405 },
  { name: 'Reading', lat: 51.4543, lng: -0.9781 },
  { name: 'Bristol', lat: 51.4545, lng: -2.5879 },
  { name: 'Cardiff', lat: 51.4816, lng: -3.1791 },
  { name: 'Newcastle upon Tyne', lat: 54.9783, lng: -1.6178 },
  { name: 'Glasgow', lat: 55.8642, lng: -4.2518 },
  { name: 'Edinburgh', lat: 55.9533, lng: -3.1883 },
  { name: 'Belfast', lat: 54.5973, lng: -5.9301 },
];

/** Distances people actually think in, rather than an open number field. */
/**
 * How far out to search, in miles — the unit people here actually think in.
 *
 * <p>Only the presentation is imperial. State holds miles and converts to metres once, in
 * `toQuery`, because the backend's radius query takes metres. Converting in more than one place is
 * how a 25-mile search starts returning events the list then calls 26 miles away.
 */
export const RADIUS_OPTIONS_MILES = [5, 10, 25, 50, 75] as const;

/** One statute mile. The single conversion factor between what's shown and what's queried. */
export const METRES_PER_MILE = 1609.344;
